import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import SectionHeader from '../components/SectionHeader';
import { NotesPanel, AssignDropdown, UrgentCheckbox, stageMap } from '../components/BlockedOrdersShared';

// Blocked (Problem Encountered / To Call Customer) sales order items that
// have received nothing at all (qty_moved = 0), grouped by the item's
// supplier -- lets staff work through blocked orders one supplier at a
// time instead of hunting through the general worklist. Backed by
// get_blocked_orders_by_supplier(), which already excludes VOID orders and
// only includes items still fully outstanding.
// Clears (or sets) one of the reason flags the escalation scripts stamp.
// Modelled on UrgentCheckbox and writes the same way -- only its own column
// plus updated_at, so it cannot clobber the assignment or the other flag.
//
// Kept local to this page rather than added to BlockedOrdersShared: the flags
// are only shown and filtered here, and that shared file has unrelated
// uncommitted work in it.
const FlagCheckbox = ({ orderId, column, stateKey, checked, label, colour, onFlagChange }) => {
  const [saving, setSaving] = useState(false);

  const handleChange = async (e) => {
    const value = e.target.checked;
    setSaving(true);
    const { error } = await supabase
      .from('blocked_order_assignments')
      .upsert({ order_id: orderId, [column]: value, updated_at: new Date().toISOString() });
    setSaving(false);
    if (error) {
      alert(`Failed to update ${label}: ${error.message}`);
      return;
    }
    onFlagChange(orderId, stateKey, value);
  };

  return (
    <label style={{
      display: 'flex', alignItems: 'center', gap: 4, fontSize: 11,
      color: checked ? colour : 'var(--muted)',
      fontWeight: checked ? 700 : 400, cursor: 'pointer',
    }}>
      <input type="checkbox" checked={!!checked} disabled={saving} onChange={handleChange} />
      {label}
    </label>
  );
};

const BlockedBySupplier = () => {
  const navigate = useNavigate();
  const [rows, setRows]           = useState([]);
  const [loading, setLoading]     = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [supplierFilter, setSupplierFilter] = useState('');
  const [reasonFilter, setReasonFilter]     = useState('');
  const [showEmailList, setShowEmailList]   = useState(false);
  const [copied, setCopied]                 = useState(false);
  // Expanded "problem encountered" panel, keyed by table row (an order ref can
  // appear on several rows here -- one per outstanding item -- so expanding one
  // row shouldn't blow open every other row for the same order).
  const [expandedRow, setExpandedRow]         = useState(null);
  const [notesByOrder, setNotesByOrder]       = useState({});
  const [assignedByOrder, setAssignedByOrder] = useState({});
  const [staffList, setStaffList]             = useState([]);
  const [nextActions, setNextActions]         = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase.rpc('get_blocked_orders_by_supplier');
    if (error) {
      console.error('get_blocked_orders_by_supplier error:', error);
      setRows([]);
    } else {
      setRows(data || []);
    }
    setLoading(false);
    setLastRefresh(new Date());
  }, []);

  // Same supporting data as BlockedOrders.jsx / BackOrderManagement.jsx, so the
  // expanded panel is the identical management surface rather than a read-only copy.
  const loadStaff = useCallback(async () => {
    const { data } = await supabase.from('staff').select('name, initials').order('name');
    setStaffList(data || []);
  }, []);

  const loadNextActions = useCallback(async () => {
    const { data } = await supabase
      .from('blocked_order_next_actions')
      .select('id, label, sort_order')
      .eq('active', true)
      .order('sort_order');
    setNextActions(data || []);
  }, []);

  const loadNotes = useCallback(async () => {
    const { data } = await supabase
      .from('blocked_order_notes')
      .select('id, order_id, note, initials, created_at, follow_up_date')
      .order('created_at', { ascending: false });
    const map = {};
    for (const n of data || []) {
      (map[n.order_id] ??= []).push(n);
    }
    setNotesByOrder(map);
  }, []);

  const loadAssignments = useCallback(async () => {
    const { data } = await supabase
      .from('blocked_order_assignments')
      .select('order_id, initials, urgent, potential_backorder, expected_not_delivered');
    const map = {};
    for (const a of data || []) {
      map[a.order_id] = {
        initials: a.initials,
        urgent:   a.urgent,
        // Why the order is blocked, stamped by whichever automation escalated
        // it -- no_stock_problem_orders.py (supplier cannot supply it) vs
        // smart_buyer_daily_po_cleanup.py (should have arrived and did not).
        // The two need different work, so they are worth telling apart at a
        // glance rather than reading the notes on every row.
        potentialBackorder:   a.potential_backorder,
        expectedNotDelivered: a.expected_not_delivered,
      };
    }
    setAssignedByOrder(map);
  }, []);

  useEffect(() => {
    load(); loadNotes(); loadStaff(); loadAssignments(); loadNextActions();
  }, [load, loadNotes, loadStaff, loadAssignments, loadNextActions]);

  const handleNoteAdded = (orderId, newNote) => {
    setNotesByOrder(prev => ({ ...prev, [orderId]: [newNote, ...(prev[orderId] || [])] }));
  };

  const handleAssign = (orderId, initials) => {
    setAssignedByOrder(prev => ({ ...prev, [orderId]: { ...(prev[orderId] || {}), initials } }));
  };

  const handleUrgentChange = (orderId, urgent) => {
    setAssignedByOrder(prev => ({ ...prev, [orderId]: { ...(prev[orderId] || {}), urgent } }));
  };

  const handleFlagChange = (orderId, stateKey, value) => {
    setAssignedByOrder(prev => ({ ...prev, [orderId]: { ...(prev[orderId] || {}), [stateKey]: value } }));
  };

  const suppliers = [...new Set(rows.map(r => r.supplier_name))].sort();
  // Supplier and reason filter stack, so "everything Bringans can't supply"
  // is reachable in two clicks -- the reason is what decides the job (chase a
  // backorder date vs chase a late delivery), so working one kind at a time
  // is the normal way to use this page.
  const filtered = rows.filter((r) => {
    if (supplierFilter && r.supplier_name !== supplierFilter) return false;
    if (!reasonFilter) return true;
    const a = assignedByOrder[r.order_id] || {};
    if (reasonFilter === 'backorder')  return !!a.potentialBackorder;
    if (reasonFilter === 'late')       return !!a.expectedNotDelivered;
    if (reasonFilter === 'urgent')     return !!a.urgent;
    // 'unflagged' -- escalated by hand, or before the flags existed, so
    // nothing recorded why. These are the ones with no automation behind them.
    if (reasonFilter === 'unflagged')  return !a.potentialBackorder && !a.expectedNotDelivered;
    return true;
  });

  const reasonCounts = rows.reduce((acc, r) => {
    const a = assignedByOrder[r.order_id] || {};
    if (a.potentialBackorder) acc.backorder += 1;
    if (a.expectedNotDelivered) acc.late += 1;
    if (a.urgent) acc.urgent += 1;
    if (!a.potentialBackorder && !a.expectedNotDelivered) acc.unflagged += 1;
    return acc;
  }, { backorder: 0, late: 0, urgent: 0, unflagged: 0 });

  const supplierCounts = rows.reduce((acc, r) => {
    acc[r.supplier_name] = (acc[r.supplier_name] || 0) + 1;
    return acc;
  }, {});

  const fmtDate = (val) => {
    if (!val) return '—';
    return new Date(val).toLocaleDateString('en-NZ', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  // One row per item_code -- an item repeated across several customer
  // orders is one line item for supplier purposes, with quantities summed
  // across every order it's outstanding on. Sorted by code so any
  // remaining duplicates (same code, different supplier code somehow) land
  // next to each other.
  const emailRows = (() => {
    const map = new Map();
    for (const r of filtered) {
      const existing = map.get(r.item_code);
      if (existing) {
        existing.qty_ordered += Number(r.qty_ordered) || 0;
      } else {
        map.set(r.item_code, { ...r, qty_ordered: Number(r.qty_ordered) || 0 });
      }
    }
    return [...map.values()].sort((a, b) => (a.item_code || '').localeCompare(b.item_code || ''));
  })();

  const emailText = [
    ['Qty', 'Item Code', 'Supplier Code', 'Order Date', 'Product Name'].join('\t'),
    ...emailRows.map(r => [
      r.qty_ordered,
      r.item_code,
      r.supplier_code || '',
      fmtDate(r.last_po_date),
      (r.item_name || '').slice(0, 50),
    ].join('\t')),
  ].join('\n');

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(emailText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API blocked (permissions/insecure context) -- the textarea
      // below is still there for a manual select-all + copy.
    }
  };

  return (
    <main className="app-main">
      <div className="page-nav">
        <button className="back-btn" onClick={() => navigate('/blocked-orders')}>← Problem Encountered</button>
        <span className="page-nav__title">Problem By Supplier</span>
        <span className="app-header__refresh">Last refresh: {lastRefresh.toLocaleTimeString('en-NZ')}</span>
        <button className="app-header__btn" onClick={load}>↻ Refresh</button>
      </div>

      <section className="section">
        <SectionHeader
          title="Blocked Order Items — Nothing Received"
          count={filtered.length}
          onRefresh={load}
          loading={loading}
          actions={
            <div style={{ display: 'flex', gap: 8 }}>
              <select
                value={supplierFilter}
                onChange={e => setSupplierFilter(e.target.value)}
                style={{
                  background: 'var(--bg3)', color: 'var(--white)',
                  border: '1px solid var(--border2)', borderRadius: 4,
                  padding: '4px 10px', fontFamily: 'var(--font-body)',
                  fontSize: 12, cursor: 'pointer', minWidth: 220,
                }}
              >
                <option value="">All Suppliers ({rows.length})</option>
                {suppliers.map(s => (
                  <option key={s} value={s}>{s} ({supplierCounts[s]})</option>
                ))}
              </select>
              <select
                value={reasonFilter}
                onChange={e => setReasonFilter(e.target.value)}
                style={{
                  background: 'var(--bg3)', color: 'var(--white)',
                  border: '1px solid var(--border2)', borderRadius: 4,
                  padding: '4px 10px', fontFamily: 'var(--font-body)',
                  fontSize: 12, cursor: 'pointer', minWidth: 200,
                }}
              >
                <option value="">Any Reason ({rows.length})</option>
                <option value="backorder">Potential backorder ({reasonCounts.backorder})</option>
                <option value="late">Expected, not delivered ({reasonCounts.late})</option>
                <option value="urgent">Urgent ({reasonCounts.urgent})</option>
                <option value="unflagged">No reason recorded ({reasonCounts.unflagged})</option>
              </select>
              <button
                className="action-btn action-btn--ghost"
                style={{ fontSize: 12 }}
                disabled={!filtered.length}
                onClick={() => setShowEmailList(true)}
              >
                📋 Copy List for Email
              </button>
            </div>
          }
        />
        <p className="section__desc">
          Items on Problem Encountered / To Call Customer orders with qty_moved = 0 -- nothing
          received against them at all -- grouped by the item's supplier, with each item's
          supplier code and most recent purchase order for reference. Click an Order Ref to
          open the same Problem Encountered management panel used on the main worklist --
          Cin7 internal comments, staff notes, next action, follow-up, assignment and urgent flag.
        </p>

        {loading && <div className="loading-row">Loading…</div>}
        {!loading && !filtered.length && (
          <div className="empty-row">✓ No fully-outstanding blocked items{supplierFilter ? ` for ${supplierFilter}` : ''}</div>
        )}

        {!loading && !!filtered.length && (
          <table className="data-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>Order Ref</th>
                <th>Customer</th>
                <th>Item Code</th>
                <th>Item Name</th>
                {!supplierFilter && <th>Supplier</th>}
                <th>Supplier Code</th>
                <th style={{ textAlign: 'right' }}>Qty</th>
                <th>Last PO</th>
                <th>Last PO Date</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r, i) => {
                const rowKey    = `${r.order_id}-${r.item_code}-${i}`;
                const isOpen    = expandedRow === rowKey;
                const stageInfo = stageMap[r.stage] || { color: 'muted', label: r.stage || 'Unknown' };
                const assigned  = assignedByOrder[r.order_id] || {};
                return (
                  <React.Fragment key={rowKey}>
                    <tr>
                      <td>
                        <button
                          onClick={() => setExpandedRow(isOpen ? null : rowKey)}
                          title="Show problem encountered notes"
                          style={{
                            background: 'none', border: 'none', padding: 0,
                            color: 'var(--gold)', cursor: 'pointer',
                            fontFamily: 'var(--font-body)', fontSize: 'inherit',
                          }}
                        >
                          {isOpen ? '▾' : '▸'} {r.order_ref}
                          {(notesByOrder[r.order_id]?.length || 0) > 0 && (
                            <span style={{ color: 'var(--muted)', marginLeft: 6, fontSize: 11 }}>
                              ({notesByOrder[r.order_id].length} note{notesByOrder[r.order_id].length === 1 ? '' : 's'})
                            </span>
                          )}
                          {assigned.urgent && (
                            <span style={{ color: 'var(--red-light, #f87171)', marginLeft: 6, fontSize: 11, fontWeight: 700 }}>
                              URGENT
                            </span>
                          )}
                          {assigned.expectedNotDelivered && (
                            <span className="reason-badge reason-badge--late">Expected, not delivered</span>
                          )}
                          {assigned.potentialBackorder && (
                            <span className="reason-badge reason-badge--backorder">Potential backorder</span>
                          )}
                        </button>
                      </td>
                      <td>{r.customer}</td>
                      <td><span className="code-badge code-badge--sm">{r.item_code}</span></td>
                      <td>{r.item_name}</td>
                      {!supplierFilter && <td>{r.supplier_name}</td>}
                      <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{r.supplier_code || '—'}</td>
                      <td style={{ textAlign: 'right' }}>{r.qty_ordered}</td>
                      <td>{r.last_po_ref || '—'}</td>
                      <td>{fmtDate(r.last_po_date)}</td>
                    </tr>
                    {isOpen && (
                      <tr>
                        <td colSpan={supplierFilter ? 8 : 9} style={{ background: 'var(--bg2)', padding: '10px 12px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                            <span className="code-badge">{r.order_ref}</span>
                            <span className={`pill pill--${stageInfo.color}`}>{stageInfo.label}</span>
                            <span style={{ fontSize: 11, color: 'var(--muted)' }}>{fmtDate(r.created_date)}</span>
                            <AssignDropdown
                              orderId={r.order_id}
                              assignedInitials={assigned.initials}
                              staffList={staffList}
                              onAssign={handleAssign}
                            />
                            <UrgentCheckbox
                              orderId={r.order_id}
                              urgent={assigned.urgent}
                              onUrgentChange={handleUrgentChange}
                            />
                            <FlagCheckbox
                              orderId={r.order_id}
                              column="potential_backorder"
                              stateKey="potentialBackorder"
                              checked={assigned.potentialBackorder}
                              label="Potential backorder"
                              colour="#92400E"
                              onFlagChange={handleFlagChange}
                            />
                            <FlagCheckbox
                              orderId={r.order_id}
                              column="expected_not_delivered"
                              stateKey="expectedNotDelivered"
                              checked={assigned.expectedNotDelivered}
                              label="Expected, not delivered"
                              colour="#1E40AF"
                              onFlagChange={handleFlagChange}
                            />
                          </div>

                          {r.internal_comments && (
                            <div style={{
                              margin: '0 12px 8px', padding: '8px 10px', fontSize: 12,
                              background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 4,
                              color: 'var(--muted)', whiteSpace: 'pre-wrap',
                            }}>
                              <strong style={{ color: 'var(--white)' }}>Internal comments (Cin7):</strong>
                              <div style={{ marginTop: 4 }}>{r.internal_comments}</div>
                            </div>
                          )}

                          <NotesPanel
                            orderId={r.order_id}
                            orderRef={r.order_ref}
                            stage={r.stage}
                            notes={notesByOrder[r.order_id] || []}
                            staffList={staffList}
                            nextActions={nextActions}
                            onAdded={handleNoteAdded}
                            allowSetBackorder
                            source="Admin Problem Encountered Page"
                          />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </section>

      {showEmailList && (
        <>
          <div
            onClick={() => setShowEmailList(false)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(13, 21, 32, 0.55)', backdropFilter: 'blur(2px)', zIndex: 1000 }}
          />
          <div style={{
            position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
            zIndex: 1001, width: 760, maxWidth: '95vw', maxHeight: '85vh',
            display: 'flex', flexDirection: 'column',
            background: '#FFFFFF', border: '1px solid #D8DEE6',
            borderRadius: 10, boxShadow: '0 24px 60px rgba(0,0,0,0.4)',
            fontFamily: 'var(--font, Helvetica, sans-serif)', overflow: 'hidden',
          }}>
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '14px 20px 12px', borderBottom: '1px solid #D8DEE6',
              background: '#F4F6F8', flexShrink: 0,
            }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#111' }}>
                📋 Copy List — {supplierFilter || 'All Suppliers'} ({emailRows.length} item{emailRows.length === 1 ? '' : 's'})
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <button onClick={handleCopy} style={{
                  background: copied ? '#4CAF50' : '#FFFFFF',
                  border: '1px solid #C8922A',
                  borderRadius: 5, color: copied ? '#FFFFFF' : '#111',
                  cursor: 'pointer', fontSize: 12, padding: '5px 12px', fontWeight: 600,
                }}>
                  {copied ? '✓ Copied' : 'Copy to Clipboard'}
                </button>
                <button onClick={() => setShowEmailList(false)} title="Close (Esc)" style={{
                  background: 'transparent', border: 'none', color: '#555',
                  cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: '2px 4px',
                }}>✕</button>
              </div>
            </div>

            <div style={{ overflowY: 'auto', flex: 1, padding: '16px 20px', background: '#FFFFFF' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, color: '#111' }}>
                <thead>
                  <tr>
                    {['Qty', 'Item Code', 'Supplier Code', 'Order Date', 'Product Name'].map(h => (
                      <th key={h} style={{
                        textAlign: h === 'Qty' ? 'right' : 'left', padding: '6px 10px', borderBottom: '1px solid #D8DEE6',
                        color: '#555', textTransform: 'uppercase', fontSize: 10,
                      }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {emailRows.map((r, i) => (
                    <tr key={r.item_code + i}>
                      <td style={{ padding: '5px 10px', textAlign: 'right', fontWeight: 700, borderBottom: '1px solid #EAEDF0', color: '#111' }}>{r.qty_ordered}</td>
                      <td style={{ padding: '5px 10px', fontFamily: 'monospace', borderBottom: '1px solid #EAEDF0', color: '#111' }}>{r.item_code}</td>
                      <td style={{ padding: '5px 10px', fontFamily: 'monospace', borderBottom: '1px solid #EAEDF0', color: '#111' }}>{r.supplier_code || '—'}</td>
                      <td style={{ padding: '5px 10px', borderBottom: '1px solid #EAEDF0', color: '#111' }}>{fmtDate(r.last_po_date)}</td>
                      <td style={{ padding: '5px 10px', borderBottom: '1px solid #EAEDF0', color: '#111' }}>{(r.item_name || '').slice(0, 50)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div style={{ marginTop: 14, fontSize: 10, color: '#555', textTransform: 'uppercase', fontWeight: 700 }}>
                Or select and copy manually
              </div>
              <textarea
                readOnly
                value={emailText}
                onFocus={e => e.target.select()}
                style={{
                  width: '100%', height: 140, marginTop: 6, resize: 'vertical',
                  background: '#FFFFFF', color: '#111',
                  border: '1px solid #D8DEE6', borderRadius: 6,
                  padding: '8px 10px', fontFamily: 'monospace', fontSize: 11,
                }}
              />
            </div>

            <div style={{
              padding: '10px 20px', borderTop: '1px solid #D8DEE6',
              background: '#F4F6F8', display: 'flex', justifyContent: 'flex-end', flexShrink: 0,
            }}>
              <button onClick={() => setShowEmailList(false)} style={{
                background: '#FFFFFF', border: '1px solid #D8DEE6',
                borderRadius: 5, color: '#111', cursor: 'pointer', fontSize: 12, padding: '5px 14px',
              }}>Close</button>
            </div>
          </div>
        </>
      )}
    </main>
  );
};

export default BlockedBySupplier;
